"use client";

import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { useUserPreferences } from "@/services/hooks/preferenceHooks";
import { useAuth } from "./auth-context";
import { Preference } from "@/models/Preference";
import { safeLocalStorage } from "@/utils/localStorageUtils";

interface TimezoneObject {
    timeZoneId: number | null;
    timeZoneName: string | null;
    timeZoneAbbrevName: string | null;
    timeZoneValue: string | null;
}

interface PreferenceContextType {
    preference: Preference | null;
    timezone: TimezoneObject;
    timeZoneId: number | null;
    timeZoneName: string | null;
    timeZoneAbbrevName: string | null;
    timeZoneValue: string | null;
    theme: string | null;
    enableSms: boolean;
    enableEmail: boolean;
    enablePush: boolean;
    isLoading: boolean;
    error: Error | null;
    refetchPreferences: () => void;
}

const PREFERENCE_STORAGE_KEY = "userPreferences";

const PreferenceContext = createContext<PreferenceContextType>({
    preference: null,
    timezone: {
        timeZoneId: null,
        timeZoneName: null,
        timeZoneAbbrevName: null,
        timeZoneValue: null,
    },
    timeZoneId: null,
    timeZoneName: null,
    timeZoneAbbrevName: null,
    timeZoneValue: null,
    theme: null,
    enableSms: false,
    enableEmail: false,
    enablePush: false,
    isLoading: false,
    error: null,
    refetchPreferences: () => {},
});

// Helper to get preferences from localStorage
const getStoredPreferences = (): Partial<Preference> | null => {
    if (!safeLocalStorage.isAvailable()) return null;

    const stored = safeLocalStorage.getItem<Partial<Preference>>(
        PREFERENCE_STORAGE_KEY
    );

    // Basic validation - ensure it's an object
    if (stored && typeof stored === "object" && !Array.isArray(stored)) {
        return stored;
    }

    return null;
};

// Helper to save preferences to localStorage
const savePreferencesToStorage = (preferences: Preference) => {
    if (!safeLocalStorage.isAvailable()) {
        console.warn(
            "localStorage is not available, preferences will not persist"
        );
        return;
    }

    // Validate preferences object before saving
    if (preferences && typeof preferences === "object") {
        safeLocalStorage.setItem(PREFERENCE_STORAGE_KEY, preferences);
    }
};

export function PreferenceProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const { userProfile, externalUserProfile, loginType } = useAuth();
    const currentProfile =
        loginType === "msal" ? userProfile : externalUserProfile;
    const userId = currentProfile?.userId;

    // Initialize with stored preferences if available
    const [storedPrefs] = useState<Partial<Preference> | null>(
        getStoredPreferences()
    );

    const {
        data: preference,
        isLoading,
        error,
        refetch,
    } = useUserPreferences(userId || 0);
    const lastPreferenceRef = useRef<Preference | null>(null);

    const [contextValue, setContextValue] = useState<PreferenceContextType>({
        preference: null,
        timezone: {
            timeZoneId: null,
            timeZoneName: null,
            timeZoneAbbrevName: null,
            timeZoneValue: null,
        },
        timeZoneId: null,
        timeZoneName: null,
        timeZoneAbbrevName: null,
        timeZoneValue: null,
        theme: null,
        enableSms: false,
        enableEmail: false,
        enablePush: false,
        isLoading: false,
        error: null,
        refetchPreferences: () => {},
    });

    // Update localStorage when preferences change
    useEffect(() => {
        if (preference && userId) {
            savePreferencesToStorage(preference);
        }
    }, [preference, userId]);

    // Update context value when preferences or loading state changes
    useEffect(() => {
        if (preference) {
            lastPreferenceRef.current = preference;
        }
    }, [preference]);

    useEffect(() => {
        // Prefer live API data, then last known API value, then stored prefs.
        const currentPrefs =
            preference || lastPreferenceRef.current || storedPrefs;

        // Extract timezone data from nested object or legacy flat structure
        let timezoneObj: TimezoneObject;

        if (
            currentPrefs?.timeZone &&
            typeof currentPrefs.timeZone === "object"
        ) {
            // New nested timezone object from API
            timezoneObj = {
                timeZoneId: currentPrefs.timeZone.timeZoneId || null,
                timeZoneName: currentPrefs.timeZone.timeZoneName || null,
                timeZoneAbbrevName: currentPrefs.timeZone.abbrevName || null,
                timeZoneValue: currentPrefs.timeZone.timeZoneValue || null,
            };
        } else {
            // Legacy flat structure or no timezone set
            timezoneObj = {
                timeZoneId: currentPrefs?.timeZoneId || null,
                timeZoneName:
                    (typeof currentPrefs?.timeZone === "string"
                        ? currentPrefs.timeZone
                        : null) || null,
                timeZoneAbbrevName: currentPrefs?.timeZoneAbbrevName || null,
                timeZoneValue: currentPrefs?.timeZoneValue || null,
            };
        }

        // Default to EST (America/New_York) if no timezone preference is set
        // This ensures acceptance criteria: "If no timezone preference, default to EST (America/New_York)"
        const DEFAULT_TIMEZONE = "America/New_York";
        const DEFAULT_TIMEZONE_NAME = "Eastern Standard Time";
        const DEFAULT_TIMEZONE_ABBREV = "EST";

        if (!timezoneObj.timeZoneValue) {
            timezoneObj.timeZoneValue = DEFAULT_TIMEZONE;
            timezoneObj.timeZoneName = DEFAULT_TIMEZONE_NAME;
            timezoneObj.timeZoneAbbrevName = DEFAULT_TIMEZONE_ABBREV;
        }

        setContextValue({
            preference: currentPrefs as Preference | null,
            timezone: timezoneObj,
            timeZoneId: timezoneObj.timeZoneId,
            timeZoneName: timezoneObj.timeZoneName,
            timeZoneAbbrevName: timezoneObj.timeZoneAbbrevName,
            timeZoneValue: timezoneObj.timeZoneValue,
            theme: currentPrefs?.theme || "light",
            enableSms: currentPrefs?.enableSms || false,
            enableEmail: currentPrefs?.enableEmail || false,
            enablePush: currentPrefs?.enablePush || false,
            isLoading,
            error: error as Error | null,
            refetchPreferences: refetch,
        });
    }, [preference, storedPrefs, isLoading, error, refetch]);

    return (
        <PreferenceContext.Provider value={contextValue}>
            {children}
        </PreferenceContext.Provider>
    );
}

export const usePreference = () => {
    const context = useContext(PreferenceContext);
    if (!context) {
        throw new Error(
            "usePreference must be used within a PreferenceProvider"
        );
    }
    return context;
};

// Utility function to convert date to user's timezone
export const convertToUserTimezone = (
    date: Date | string,
    timeZoneValue?: string | null
): Date => {
    if (!timeZoneValue) return new Date(date);

    try {
        const dateObj = typeof date === "string" ? new Date(date) : date;
        // Create a date in the specified timezone
        const options: Intl.DateTimeFormatOptions = {
            timeZone: timeZoneValue,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        };

        const formatter = new Intl.DateTimeFormat("en-US", options);
        const parts = formatter.formatToParts(dateObj);

        const dateParts: Record<string, string> = {};
        parts.forEach((part) => {
            dateParts[part.type] = part.value;
        });

        return new Date(
            `${dateParts.year}-${dateParts.month}-${dateParts.day}T${dateParts.hour}:${dateParts.minute}:${dateParts.second}`
        );
    } catch (error) {
        console.error("Error converting to user timezone:", error);
        return new Date(date);
    }
};

// Utility function to format date in user's timezone
export const formatInUserTimezone = (
    date: Date | string,
    timeZoneValue?: string | null,
    format?: Intl.DateTimeFormatOptions
): string => {
    if (!timeZoneValue) {
        return new Date(date).toLocaleString();
    }

    try {
        const dateObj = typeof date === "string" ? new Date(date) : date;
        const options: Intl.DateTimeFormatOptions = {
            timeZone: timeZoneValue,
            ...format,
        };

        return dateObj.toLocaleString("en-US", options);
    } catch (error) {
        console.error("Error formatting in user timezone:", error);
        return new Date(date).toLocaleString();
    }
};
